import { FC, useMemo, useState } from 'react';
import { get } from 'lodash';
import { useTheme } from 'styled-components';
import { Button, Input } from '@packages/utils';
import { Modal } from 'components/Base/Modal';
import { Form, FormItem } from 'components/Base/Form';
import { Select } from 'components/Base/Select';
import { DatePicker } from 'components/Base/DatePicker';
import { EIconNames, Icon } from 'components/Base/Icon';
import {
  ENotificationType,
  useShowToastNotification,
} from 'components/Base/Notification';
import useGetValidationData from 'components/Base/Form/validationRules';
import useForm from 'hooks/useForm';
import { TValidateFieldsError } from 'data/types/generic.types';
import { WORKSHOP_TASK_DATE_FORMAT } from 'data/types/workshopTasks.types';
import { useGetWorkshopByIdQuery } from 'redux/apiSlice/workshops.slice';
import { useAddCustomWorkshopTaskMutation } from 'redux/apiSlice/workshopTasks.slice';
import { getCustomTaskPartOptions } from './partOptions';
import {
  TAddCustomTaskFormValues,
  TAddCustomTaskProps,
} from './AddCustomTask.types';

// "Add Task" header action of the Tasks tab (GROW-4215): a one-off task for
// this workshop only. Title, Part and Due Date are all required by the BE.
// The Tasks tab is hardcoded English today, so these strings follow suit.
const AddCustomTask: FC<TAddCustomTaskProps> = ({ workshopId }) => {
  const theme = useTheme();
  const { form } = useForm<TAddCustomTaskFormValues>();
  const { validationRules } = useGetValidationData();
  const { showToastNotification } = useShowToastNotification();
  const [open, setOpen] = useState(false);
  const [addCustomTask, { isLoading }] = useAddCustomWorkshopTaskMutation();

  // Same cached query WorkShopDetails already holds; no extra request.
  const { data: workshop } = useGetWorkshopByIdQuery(
    { id: workshopId },
    { skip: !workshopId },
  );
  const partOptions = useMemo(
    () => getCustomTaskPartOptions(workshop?.parts),
    [workshop?.parts],
  );

  const handleClose = () => {
    form.resetFields();
    setOpen(false);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      await addCustomTask({
        workshopId,
        name: values.name.trim(),
        partType: values.partType,
        dueDate: values.dueDate.format(WORKSHOP_TASK_DATE_FORMAT),
      }).unwrap();
      showToastNotification({
        message: 'Task has been successfully added',
        type: ENotificationType.SUCCESS,
      });
      handleClose();
    } catch (error) {
      // Form validation errors are already shown inline; keep the modal open
      // on API errors too so the user can retry without retyping.
      if (!(error as TValidateFieldsError)?.errorFields) {
        showToastNotification({
          message:
            get(error, 'data.message') ||
            'Could not add the task. Please try again.',
          type: ENotificationType.ERROR,
        });
      }
    }
  };

  return (
    <>
      <Button
        color="primary"
        type="primary"
        onClick={() => setOpen(true)}
        icon={<Icon icon={EIconNames.PLUS} color={theme.colorWhitePrimary} />}
        data-cy="add-custom-task-btn"
      >
        Add Task
      </Button>
      {open && (
        <Modal
          title="Add Task"
          open={open}
          onCancel={handleClose}
          onOk={handleSubmit}
          okText="Add"
          cancelText="Cancel"
          confirmLoading={isLoading}
        >
          <Form form={form} layout="vertical">
            <FormItem
              label="Task Title"
              name="name"
              rules={[validationRules.requiredField()]}
            >
              <Input
                placeholder="Enter task title"
                maxLength={250}
                autoFocus
                data-cy="custom-task-title-input"
              />
            </FormItem>
            <FormItem
              label="Part"
              name="partType"
              rules={[validationRules.requiredField()]}
            >
              <Select
                options={partOptions}
                placeholder="Select part"
                data-cy="custom-task-part-select"
              />
            </FormItem>
            <FormItem
              label="Due Date"
              name="dueDate"
              rules={[validationRules.requiredField()]}
            >
              <DatePicker
                className="w-100"
                format={WORKSHOP_TASK_DATE_FORMAT}
                placeholder={WORKSHOP_TASK_DATE_FORMAT}
                showToday={false}
                data-cy="custom-task-due-date"
              />
            </FormItem>
          </Form>
        </Modal>
      )}
    </>
  );
};

export default AddCustomTask;
